import { useState } from 'react'
import { Repeat, Pencil, Trash2 } from 'lucide-react'
import { MeetingDetail } from './MeetingDetail'
import { SpaceTag } from './SpaceTag'
import { RecordButton } from './RecordButton'
import { ShareButton } from './ShareButton'
import { UpcomingNotes } from './UpcomingNotes'
import { cachedAccess } from '@/data/meetings'
import type { Meeting, RecordingMode, ShareScope } from '@/data/types'
import { cn } from '@/lib/utils'

/**
 * Only states a reader has to act on are named at all.
 *
 * Processed is the normal outcome for a past meeting and upcoming is the
 * normal state of an upcoming one, so labelling either says nothing. What is
 * left is an exception, and an exception belongs in the metadata line as a
 * coloured word — not as a badge in the action cluster, where its variable
 * width knocked every control out of alignment down the list.
 */
const MARKERS = {
  live: { label: 'Live', className: 'text-[var(--color-danger)]' },
  processing: { label: 'Processing', className: 'text-[var(--color-warning)]' },
  failed: { label: 'Failed', className: 'text-[var(--color-danger)]' },
}

function timeLabel(meeting: Meeting): string {
  const format = (value: string) =>
    new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${format(meeting.startsAt)}–${format(meeting.endsAt)}`
}

function participantLabel(meeting: Meeting): string | null {
  const names = meeting.participants.map((person) => person.name.split(' ')[0]).filter(Boolean)
  if (names.length === 0) return null
  if (names.length <= 3) return names.join(', ')
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
}

/**
 * The title, edited where it is read.
 *
 * A meeting's name arrives from the calendar or from the recorder's own client,
 * and both produce "Teams call – 2026-07-24 21:13 UTC" often enough that a list
 * of them is unreadable. Renaming is therefore common, and a dialog for a
 * single line of text is three clicks around one.
 *
 * The pencil appears on hover rather than always: a row is read far more often
 * than it is renamed, and a permanent affordance on every row is visual noise
 * proportional to the length of the list. Keyboard users reach it by tabbing —
 * `focus-within` shows it too.
 */
export interface MeetingRowHandlers {
  onSpaceChange: (meetingId: string, spaceId: string | null) => void
  onRename: (meetingId: string, title: string) => void
  onDelete: (meetingId: string) => void
  onRecordingChange: (meetingId: string, recording: RecordingMode) => void
  onToggleTemplate: (meetingId: string, templateId: string) => void
  onAddBlankNote: (meetingId: string, title: string) => void
  onAddPromptNote: (meetingId: string, prompt: string) => void
  onNoteScopeChange: (meetingId: string, scope: 'meeting' | 'series') => void
  onOrganizationAccessChange: (meetingId: string, scope: ShareScope) => void
  onPersonAccessChange: (
    meetingId: string,
    email: string,
    grant: ShareScope,
    language?: string
  ) => void
  onAddPerson: (meetingId: string, email: string, scope: ShareScope, language: string) => void
  onShareAll: (meetingId: string, scope: 'meeting' | 'series', language: string) => void
  onAddNote: (meetingId: string, templateId: string) => void
  onEditNote: (meetingId: string, noteId: string, body: string) => void
  onToggleNoteShared: (meetingId: string, noteId: string, shared: boolean) => void
  onRemoveNote: (meetingId: string, noteId: string) => void
}

/**
 * One meeting, in three fixed zones: the space code, the meeting itself, and
 * the two controls. Every zone has the same width on every row, so the list
 * reads down as columns rather than as a ragged edge.
 */
export function MeetingRow({
  meeting,
  expanded,
  onToggle,
  busy,
  handlers,
}: {
  meeting: Meeting
  expanded: boolean
  onToggle: () => void
  busy?: boolean
  handlers: MeetingRowHandlers
}) {
  const marker = MARKERS[meeting.status as keyof typeof MARKERS]
  const people = participantLabel(meeting)

  return (
    /**
     * An open meeting is bracketed out of the list rather than merely tinted.
     *
     * Three things do it together, and none of them alone was enough: the
     * whole block drops to the sunken surface so the white note cards read as
     * sitting IN something; its top and bottom rules step up from the hairline
     * divider to a full border; and a rule runs down the left edge. That rule
     * is `--color-fg`, not the brand colour — it inverts correctly in dark
     * mode and keeps the list monochrome.
     *
     * `-mt-px` pulls the top border onto the previous row's bottom one, so the
     * pair does not read as a 2px seam.
     */
    <div
      data-meeting={meeting.id}
      className={cn(
        'group border-b last:border-b-0',
        expanded
          ? 'relative -mt-px border-y border-[var(--color-border-strong)] bg-[var(--color-surface-sunken)] shadow-[inset_3px_0_0_var(--color-fg)] first:mt-0'
          : 'border-[var(--color-border-subtle)]'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2.5 px-2.5',
          expanded ? '' : 'hover:bg-[var(--color-surface-hover)]'
        )}
      >
        <SpaceTag
          spaceId={meeting.spaceId}
          busy={busy}
          onChange={(spaceId) => handlers.onSpaceChange(meeting.id, spaceId)}
        />

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35"
        >
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              {/* No hue on the title either — the row background on hover and
                  the bracketed block when open carry the state on their own. */}
              <EditableTitle
                title={meeting.title}
                canRename={meeting.canManage}
                onRename={(next) => handlers.onRename(meeting.id, next)}
              />
              {meeting.isRecurring ? (
                <Repeat
                  className="size-2.5 shrink-0 text-[var(--color-fg-subtle)]"
                  aria-label="Recurring"
                />
              ) : null}
            </span>
            <span className="mt-0.5 block truncate text-2xs text-[var(--color-fg-subtle)]">
              <span className="tnum">{timeLabel(meeting)}</span>
              {people ? ` · ${people}` : ''}
              {marker ? (
                <>
                  {' · '}
                  <span className={marker.className}>{marker.label}</span>
                </>
              ) : null}
            </span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-0.5">
          {/* Leftmost, and only on hover.
              Leftmost so it holds one position on every row: the controls to
              its right come and go — the recording toggle is upcoming-only —
              and a delete that slides under the cursor as rows change is a
              delete that gets hit by accident. On hover because a row is read
              far more often than it is deleted, and a permanent affordance on
              every row is noise proportional to the length of the list.
              Keyboard users reach it by tabbing, which is why focus shows it
              too. Whether this account may delete at all is the organisation's
              policy, enforced by the server. */}
          {meeting.canDelete ? (
            <button
              type="button"
              aria-label="Delete meeting"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation()
                handlers.onDelete(meeting.id)
              }}
              className="grid size-6 shrink-0 place-items-center rounded-[var(--radius-xs)] text-[var(--color-fg-subtle)] opacity-0 transition-[opacity,color] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-danger)] focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-30"
            >
              <Trash2 className="size-3" />
            </button>
          ) : null}

          {meeting.domain === 'upcoming' ? (
            <RecordButton
              mode={meeting.recording}
              isRecurring={meeting.isRecurring}
              busy={busy}
              onChange={(recording) => handlers.onRecordingChange(meeting.id, recording)}
            />
          ) : null}

          <ShareButton
            title={meeting.title}
            access={cachedAccess(meeting.id)}
            isRecurring={meeting.isRecurring}
            busy={busy}
            onOrganizationChange={(scope) => handlers.onOrganizationAccessChange(meeting.id, scope)}
            onPersonChange={(email, grant, language) =>
              handlers.onPersonAccessChange(meeting.id, email, grant, language)
            }
            onAddPerson={(email, scope, language) =>
              handlers.onAddPerson(meeting.id, email, scope, language)
            }
            onShareAll={(scope, language) => handlers.onShareAll(meeting.id, scope, language)}
          />

        </div>
      </div>

      {expanded ? (
        meeting.domain === 'upcoming' ? (
          <UpcomingNotes
            meeting={meeting}
            busy={busy}
            onToggleTemplate={(templateId) => handlers.onToggleTemplate(meeting.id, templateId)}
            onScopeChange={(scope) => handlers.onNoteScopeChange(meeting.id, scope)}
            onAddPromptNote={(prompt) => handlers.onAddPromptNote(meeting.id, prompt)}
            onToggleNoteShared={(noteId, shared) =>
              handlers.onToggleNoteShared(meeting.id, noteId, shared)
            }
            onRemoveNote={(noteId) => handlers.onRemoveNote(meeting.id, noteId)}
          />
        ) : (
          <MeetingDetail
            meeting={meeting}
            busy={busy}
            onAddNote={(templateId) => handlers.onAddNote(meeting.id, templateId)}
            onAddBlankNote={(title) => handlers.onAddBlankNote(meeting.id, title)}
            onAddPromptNote={(prompt) => handlers.onAddPromptNote(meeting.id, prompt)}
            onEditNote={(noteId, body) => handlers.onEditNote(meeting.id, noteId, body)}
            onToggleNoteShared={(noteId, shared) =>
              handlers.onToggleNoteShared(meeting.id, noteId, shared)
            }
            onRemoveNote={(noteId) => handlers.onRemoveNote(meeting.id, noteId)}
          />
        )
      ) : null}
    </div>
  )
}

/**
 * A title that becomes an input where it sits.
 *
 * It is inside the row's expand button, so every interactive part stops the
 * click from reaching it — otherwise renaming a meeting would also open it, and
 * pressing Escape would collapse the row instead of abandoning the edit.
 */
function EditableTitle({
  title,
  canRename,
  onRename,
}: {
  title: string
  canRename: boolean
  onRename: (title: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(title)

  // Not everyone may rename every meeting, and a pencil that always 403s is
  // worse than no pencil.
  if (!canRename) {
    return <span className="truncate text-xs font-medium text-[var(--color-fg)]">{title}</span>
  }

  if (editing) {
    const commit = () => {
      const next = draft.trim()
      if (next && next !== title) onRename(next)
      setEditing(false)
    }
    return (
      <input
        autoFocus
        value={draft}
        aria-label="Meeting name"
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === 'Enter') commit()
          // Escape abandons the edit rather than saving it — the same key that
          // means "never mind" everywhere else.
          if (event.key === 'Escape') {
            setDraft(title)
            setEditing(false)
          }
        }}
        className="min-w-0 flex-1 rounded-[var(--radius-xs)] border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-1 py-0.5 text-xs font-medium text-[var(--color-fg)] outline-none"
      />
    )
  }

  return (
    <span className="group/title flex min-w-0 items-center gap-1">
      <span className="truncate text-xs font-medium text-[var(--color-fg)]">{title}</span>
      <span
        role="button"
        tabIndex={0}
        aria-label="Rename meeting"
        onClick={(event) => {
          event.stopPropagation()
          setDraft(title)
          setEditing(true)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          event.stopPropagation()
          setDraft(title)
          setEditing(true)
        }}
        className="shrink-0 cursor-pointer rounded-[var(--radius-xs)] p-0.5 text-[var(--color-fg-subtle)] opacity-0 transition-opacity hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)] focus-visible:opacity-100 group-hover/title:opacity-100"
      >
        <Pencil className="size-2.5" />
      </span>
    </span>
  )
}
